import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Button, Box, Chip, Menu, MenuItem } from "@mui/material";
import { useTheme } from "@emotion/react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

// Components
import Contact from "../modals/Contact";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import ConfirmationModal from "../utils/ConfirmationModal";
import ImportContacts from "../modals/ImportContacts";
import Plans from "../modals/Plans";
import LoadingButton from "../utils/LoadingButton";

// Localize
import { useTranslation } from "react-i18next";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";

export default function ContactsTab() {
  const theme = useTheme();
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [planData, setPlanData] = useState([]);
  const [profileData, setProfileData] = useState([]);
  const [importMenuPosition, setImportMenuPosition] = useState(null);
  const [importButtonLoading, setImportButtonLoading] = useState(false);
  const { t } = useTranslation();

  // Context
  const { teamId, profileId } = useAuth();
  const { showNotification } = useNotification();

  const phoneFilterOperators = getGridStringOperators().map((op) => {
    if (op.value === "contains") {
      return {
        ...op,
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) {
            return null;
          }
          const rawFilter = String(filterItem.value).trim().toLowerCase();
          const digitsFilter = rawFilter.replace(/\D/g, "");
          const normalizedFilterDigits =
            digitsFilter.startsWith("521") && digitsFilter.length > 10
              ? digitsFilter.slice(3)
              : digitsFilter.startsWith("52") && digitsFilter.length > 10
              ? digitsFilter.slice(2)
              : digitsFilter;

          return ({ value }) => {
            if (!value) return false;
            const rawValue = String(value).toLowerCase();
            const digitsValue = rawValue.replace(/\D/g, "");

            if (rawValue.includes(rawFilter)) return true;
            if (
              normalizedFilterDigits &&
              digitsValue.includes(normalizedFilterDigits)
            ) {
              return true;
            }
            return false;
          };
        },
      };
    }
    return op;
  });

  const columns = [
    {
      field: "first_name",
      headerName: t("contactsTab.columns.firstName"),
      width: 130,
    },
    {
      field: "last_name",
      headerName: t("contactsTab.columns.lastName"),
      width: 130,
    },
    {
      field: "nickname",
      headerName: t("contactsTab.columns.nickname"),
      width: 130,
    },
    {
      field: "description",
      headerName: t("contactsTab.columns.description"),
      width: 130,
    },
    {
      field: "birthday",
      headerName: t("contactsTab.columns.birthday"),
      width: 130,
    },
    {
      field: "countrycode_id",
      headerName: t("contactsTab.columns.countryCode"),
      width: 110,
    },
    {
      field: "phone",
      headerName: t("contactsTab.columns.phone"),
      width: 130,
      filterOperators: phoneFilterOperators,
    },
    {
      field: "birthday_template_id",
      headerName: t("contactsTab.columns.birthdayTemplate"),
      width: 130,
      valueGetter: (params) => params.row.birthday_template_id?.name || "",
    },
    {
      field: "is_whatsapp_group",
      headerName: t("contactsTab.columns.contactType"),
      width: 120,
      renderCell: (params) => {
        const isGroup = params.row.is_whatsapp_group;
        const chipColor = isGroup ? "darkOrange" : "green";
        const label = isGroup
          ? t("contactsTab.columns.group")
          : t("contactsTab.columns.contact");

        return (
          <Chip
            label={label}
            color="primary"
            variant="outlined"
            sx={{
              borderColor: chipColor,
              color: chipColor,
              height: "20px",
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchContacts = async () => {
      let allContacts = [];
      let offset = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await supabase
          .from("contacts")
          .select(
            `
              id,
              groups_ids,
              first_name,
              last_name,
              nickname,
              description,
              birthday,
              countrycode_id,
              phone,
              variables,
              birthday_template_id(
                id,
                name
              ),
              is_whatsapp_group
            `
          )
          .order("created_at", { ascending: false })
          .range(offset, offset + pageSize - 1);

        if (error) {
          console.error("Error fetching contacts:", error);
          break;
        }

        if (data && data.length > 0) {
          allContacts = [...allContacts, ...data];
          setContacts([...allContacts]);
        }

        if (!data || data.length < pageSize) {
          break;
        }

        offset += pageSize;
      }
    };

    const fetchPlan = async () => {
      const { data, error } = await supabase
        .from("teams")
        .select(
          "plan(import_contacts_csv, import_contacts_whatsapp, birthday_messages)"
        )
        .single();

      if (!error) {
        setPlanData(data.plan);
      }
    };

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("assigned_server")
        .single();

      if (!error) {
        setProfileData(data);
      }
    };

    fetchContacts();
    fetchPlan();
    fetchProfile();
  }, [
    open,
    showDeleteConfirmation,
    importModalOpen,
    plansModalOpen,
    importButtonLoading,
  ]);

  const handleNewContact = () => {
    trackEvent("contactstab-new_contact_clicked");
    setIsEditing(false);
    setSelectedContact(null);
    handleOpen();
  };

  const handleSubmit = async () => {
    trackEvent("contactstab-new_contact_submitted");
    setOpen(false);
    setSelectedContact(null);
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    trackEvent("contactstab-contact-modal-closed");
    setOpen(false);
    setIsEditing(false);
    setSelectedContact(null);
  };

  const handleEdit = (contact) => {
    trackEvent("contactstab-contact_row_clicked");
    setIsEditing(true);
    setSelectedContact(contact);
    handleOpen();
  };

  const handleRowSelection = (selection) => {
    trackEvent("contactstab-contact_selected");
    setSelectedRows(selection);
  };

  const handleDeleteConfirmation = () => {
    trackEvent("contactstab-delete_clicked");
    setShowDeleteConfirmation(true);
  };

  const handleDeleteCancellation = () => {
    trackEvent("contactstab-delete_cancelled");
    setShowDeleteConfirmation(false);
  };

  const handleDelete = async () => {
    trackEvent("contactstab-delete_confirmed");
    try {
      const { error } = await supabase
        .from("contacts")
        .delete()
        .in("id", selectedRows.slice(0, 100));

      if (error) {
        throw error;
      }

      setSelectedRows([]);
      showNotification(t("contactsTab.contactsDeleted"), "success");
      setShowDeleteConfirmation(false);
    } catch (error) {
      showNotification(
        t("contactsTab.contactsDeletedError", { error: error.message }),
        "error"
      );
    }
  };

  const handleImportButtonClicked = (event) => {
    trackEvent("contactstab-import_clicked");
    setImportMenuPosition({
      x: event.pageX,
      y: event.pageY,
    });
  };

  const handleCloseImportMenu = () => {
    trackEvent("contactstab-import_menu_closed");
    setImportMenuPosition(null);
  };

  const handleImportOption = async (option) => {
    trackEvent("contactstab-import_item_clicked", { option });
    setImportMenuPosition(null);
    if (planData.import_contacts_csv) {
      if (option === "whatsapp") {
        if (planData.import_contacts_whatsapp) {
          setImportButtonLoading(true);
          try {
            const body = {
              teamId: teamId,
              profileId: profileId,
              baseUrl: profileData.assigned_server,
            };

            const whatsAppImportResponse = await fetch(
              "https://importwhatsappcontacts-tiph5o4x3q-uc.a.run.app",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
              }
            );

            if (!whatsAppImportResponse.ok) {
              throw new Error(whatsAppImportResponse.status);
            }

            const whatsAppResponseJson = await whatsAppImportResponse.json();

            if (!whatsAppResponseJson.success) {
              throw new Error(whatsAppResponseJson.message);
            }

            showNotification(
              t("contactsTab.importWhatsAppContactsSuccess"),
              "success"
            );
          } catch (error) {
            showNotification(
              t("contactsTab.importWhatsAppContactsFailed", {
                error: error,
              }),
              "error"
            );
          } finally {
            setImportButtonLoading(false);
          }
        } else {
          setPlansModalOpen(true);
          showNotification(t("contactsTab.importWhatsAppNotInPlan"), "warning");
        }
      } else {
        setImportModalOpen(true);
      }
    } else {
      setPlansModalOpen(true);
      showNotification(t("contactsTab.importNotInPlan"), "warning");
    }
  };

  const handleCloseImportModal = () => {
    trackEvent("contactstab-import_modal_closed");
    setImportModalOpen(false);
  };

  const handleClosePlansModal = () => {
    trackEvent("contactstab-plans_modal_closed");
    setPlansModalOpen(false);
  };

  return (
    <Box>
      <Box
        sx={{
          height: "auto",
          minHeight: "400px",
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
            bgcolor: "transparent",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottom: "none",
            color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
          },
          "& .MuiCheckbox-root": {
            color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
            fontWeight: "bold",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          },
        }}
      >
        <DataGrid
          autoHeight
          rows={contacts}
          columns={columns}
          checkboxSelection
          onRowClick={(params) => handleEdit(params.row)}
          disableSelectionOnClick
          rowSelectionModel={selectedRows}
          onSelectionModelChange={handleRowSelection}
          sx={{ borderRadius: "16px", overflow: "hidden" }}
          components={{
            Toolbar: () => (
              <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewContact}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: "bold",
                    background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                    "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                  }}
                >
                  {t("contactsTab.toolbar.newContact")}
                </Button>

                <LoadingButton
                  variant="contained"
                  startIcon={<FileUploadOutlinedIcon />}
                  onClick={handleImportButtonClicked}
                  loading={importButtonLoading}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: "bold",
                    background: "linear-gradient(90deg, #4cceac 0%, #3da58a 100%)",
                    color: "#141b2d",
                    boxShadow: "0 4px 14px 0 rgba(76, 206, 172, 0.39)",
                    "&:hover": { background: "linear-gradient(90deg, #3da58a 0%, #2e7c67 100%)", color: "#fff" }
                  }}
                >
                  {t("contactsTab.toolbar.importContacts")}
                </LoadingButton>

                {selectedRows.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteOutlinedIcon />}
                    color="error"
                    onClick={handleDeleteConfirmation}
                    sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" } }}
                  >
                    {t("contactsTab.toolbar.delete")}
                  </Button>
                )}

                <Box sx={{ flexGrow: 1 }} />

                <GridToolbarFilterButton />
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport />
              </GridToolbarContainer>
            ),
          }}
        />
      </Box>

      <Contact
        open={open}
        onClose={handleClose}
        isEditing={isEditing}
        contact={selectedContact}
        planData={planData}
        setPlansModalOpen={setPlansModalOpen}
        onSubmit={handleSubmit}
      />

      <ConfirmationModal
        open={showDeleteConfirmation}
        onClose={handleDeleteCancellation}
        onConfirm={handleDelete}
        title={t("contactsTab.confirmationModal.title")}
        message={t("contactsTab.confirmationModal.message")}
      />

      <Menu
        open={Boolean(importMenuPosition)}
        onClose={handleCloseImportMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          importMenuPosition
            ? { top: importMenuPosition.y, left: importMenuPosition.x }
            : undefined
        }
      >
        <MenuItem onClick={() => handleImportOption("whatsapp")}>
          {t("contactsTab.toolbar.importFromWhatsApp")}
        </MenuItem>
        <MenuItem onClick={() => handleImportOption("csv")}>
          {t("contactsTab.toolbar.importFromCsv")}
        </MenuItem>
      </Menu>
      <ImportContacts open={importModalOpen} onClose={handleCloseImportModal} />
      <Plans open={plansModalOpen} onClose={handleClosePlansModal} />
    </Box>
  );
}
